/**
 * Shared types for LIMS API (server and client)
 *
 * These types are used by both the API server and the API client to ensure
 * type consistency across the API boundary.
 *
 * The *_ATTRIBUTE_DEFS arrays are the SINGLE source of truth for which
 * attributes exist per entity type. They serve two purposes:
 *   1. Runtime: the server registers them in the DB at startup.
 *   2. Compile-time: TypeScript attribute interfaces are DERIVED from them
 *      via mapped types — no manual duplication needed.
 */
/**
 * Creates a validator that normalizes text values to a canonical form.
 * Matching is case-insensitive. Unrecognised values are rejected.
 */
export function textEnum(canonicalValues, aliases) {
    const lowerMap = new Map();
    for (const v of canonicalValues)
        lowerMap.set(v.toLowerCase(), v);
    if (aliases) {
        for (const [alias, canonical] of Object.entries(aliases))
            lowerMap.set(alias.toLowerCase(), canonical);
    }
    return (value) => {
        const lower = String(value).toLowerCase();
        const canonical = lowerMap.get(lower);
        if (canonical !== undefined)
            return { value: canonical };
        return { error: `Invalid value '${value}'. Allowed: ${canonicalValues.join(', ')}` };
    };
}
// ============================================================================
// Attribute definitions (runtime + compile-time source of truth)
//
// To add a new attribute: add one entry here. The TypeScript type updates
// automatically — no separate interface to maintain.
// ============================================================================
export const SAMPLE_ATTRIBUTE_DEFS = [
    { name: 'attachment_count', data_type: 'number' },
    { name: 'batch_number', data_type: 'text' },
    { name: 'collection_site', data_type: 'text' },
    { name: 'collection_timestamp', data_type: 'date' },
    { name: 'complete', data_type: 'boolean' },
    { name: 'date_completed', data_type: 'date' },
    { name: 'date_created', data_type: 'date' },
    { name: 'date_received', data_type: 'date' },
    { name: 'has_report', data_type: 'boolean' },
    { name: 'id_hash', data_type: 'text' },
    { name: 'last_updated', data_type: 'date' },
    { name: 'linked', data_type: 'boolean' },
    { name: 'lot_number', data_type: 'text' },
    { name: 'mm_is_top_priority', data_type: 'boolean' },
    { name: 'mm_los_experiment_campaign_description', data_type: 'text' },
    { name: 'mm_los_experiment_campaign_id', data_type: 'text' },
    { name: 'mm_los_experiment_campaign_name', data_type: 'text' },
    { name: 'mm_los_experiment_description', data_type: 'text' },
    { name: 'mm_los_experiment_id', data_type: 'text' },
    { name: 'mm_los_experiment_name', data_type: 'text' },
    { name: 'mm_los_experiment_unit_operation_id', data_type: 'text' },
    { name: 'mm_los_experiment_unit_operation_name', data_type: 'text' },
    { name: 'mm_needed_by_hours', data_type: 'text' },
    { name: 'mm_priority_reason', data_type: 'text' },
    { name: 'mm_program_id', data_type: 'text' },
    { name: 'mm_sample_lab_process_type', data_type: 'text' },
    { name: 'mm_sample_type', data_type: 'text' },
    { name: 'order_id', data_type: 'number', referenced_entity_type: 'order' },
    { name: 'order_request', data_type: 'boolean' },
    { name: 'parent_batch_number', data_type: 'text' },
    { name: 'partially_complete', data_type: 'boolean' },
    { name: 'received', data_type: 'boolean' },
    { name: 'report_emailed', data_type: 'boolean' },
    { name: 'retain_period_weeks', data_type: 'text' },
    { name: 'sample_description', data_type: 'text' },
    { name: 'sample_matrix', data_type: 'text' },
    { name: 'sample_name', data_type: 'text' },
    { name: 'sample_point', data_type: 'text' },
    { name: 'sample_received', data_type: 'boolean' },
    { name: 'sample_type', data_type: 'text' },
    { name: 'stream_type', data_type: 'text' },
    { name: 'test_count', data_type: 'number' },
    { name: 'unit_number', data_type: 'text' },
    { name: 'collection_datestamp', data_type: 'text' },
    { name: 'lab_id', data_type: 'number', referenced_entity_type: 'lab' },
    { name: 'parsed_from', data_type: 'text' },
    { name: 'pilot_data', data_type: 'json' },
];
export const TEST_ATTRIBUTE_DEFS = [
    { name: 'assay_id', data_type: 'number', referenced_entity_type: 'assay' },
    { name: 'lab_id', data_type: 'number', referenced_entity_type: 'lab' },
    { name: 'comments', data_type: 'text' },
    { name: 'complete_date', data_type: 'date' },
    { name: 'date_created', data_type: 'date' },
    { name: 'emailed', data_type: 'boolean' },
    { name: 'has_report', data_type: 'boolean' },
    { name: 'last_updated', data_type: 'text' },
    { name: 'last_updated_unix_timestamp', data_type: 'number' },
    { name: 'parent_status', data_type: 'text' },
    { name: 'publish_worksheet_to_portal', data_type: 'boolean' },
    { name: 'raw_worksheet_data', data_type: 'json' },
    { name: 'release_report', data_type: 'boolean' },
    { name: 'report_emailed', data_type: 'boolean' },
    { name: 'results', data_type: 'text' },
    { name: 'sample_id', data_type: 'number', referenced_entity_type: 'sample' },
    { name: 'specification_overall_status', data_type: 'text' },
    { name: 'start_date', data_type: 'date' },
    { name: 'state', data_type: 'text', validate: textEnum(['NOT STARTED', 'IN PROGRESS', 'COMPLETED', 'CANCELLED'], { pending: 'NOT STARTED', started: 'IN PROGRESS', in_progress: 'IN PROGRESS', complete: 'COMPLETED', canceled: 'CANCELLED' }) },
    { name: 'tech_id', data_type: 'number' },
    { name: 'worksheet_data', data_type: 'json' },
    { name: 'worksheet_processed', data_type: 'boolean' },
    { name: 'worksheet_processed_error', data_type: 'boolean' },
    { name: 'parsed_from', data_type: 'text' },
];
export const ASSAY_ATTRIBUTE_DEFS = [
    { name: 'active', data_type: 'boolean' },
    { name: 'batch_worksheet_id', data_type: 'number' },
    { name: 'date_created', data_type: 'date' },
    { name: 'default_technician_id', data_type: 'number' },
    { name: 'last_updated', data_type: 'date' },
    { name: 'mm_run_time_min', data_type: 'number' },
    { name: 'sample_level_worksheet', data_type: 'boolean' },
    { name: 'show_in_portal', data_type: 'boolean' },
    { name: 'title', data_type: 'text' },
    { name: 'worksheet_id', data_type: 'number' },
];
export const ORDER_ATTRIBUTE_DEFS = [
    { name: 'customer_account_id', data_type: 'number' },
    { name: 'date_approved', data_type: 'date' },
    { name: 'date_completed', data_type: 'date' },
    { name: 'date_created', data_type: 'date' },
    { name: 'date_received', data_type: 'date' },
    { name: 'date_requested', data_type: 'date' },
    { name: 'division_id', data_type: 'number' },
    { name: 'entered_by_api_client_id', data_type: 'text' },
    { name: 'entered_by_id', data_type: 'number' },
    { name: 'last_updated', data_type: 'date' },
    { name: 'order_request', data_type: 'boolean' },
    { name: 'order_request_status', data_type: 'text', validate: textEnum(['DRAFT', 'SUBMITTED', 'RECEIVED', 'APPROVED']) },
    { name: 'portal_read', data_type: 'boolean' },
    { name: 'published_to_portal', data_type: 'boolean' },
    { name: 'received_by_id', data_type: 'number' },
    { name: 'requested_by_id', data_type: 'number' },
    { name: 'state', data_type: 'text', validate: textEnum(['CREATED', 'RECEIVED', 'IN PROGRESS', 'COMPLETED', 'CANCELLED'], { in_progress: 'IN PROGRESS', complete: 'COMPLETED', canceled: 'CANCELLED' }) },
    { name: 'submitted_by', data_type: 'text' },
    { name: 'total_samples_created', data_type: 'number' },
    { name: 'lab_id', data_type: 'number', referenced_entity_type: 'lab' },
];
export const ATTACHMENT_ATTRIBUTE_DEFS = [
    { name: 'parent_entity_id', data_type: 'number' },
    { name: 'parent_entity_type', data_type: 'text' },
    { name: 'filename', data_type: 'text' },
    { name: 'content_type', data_type: 'text' },
    { name: 'content_hash', data_type: 'text' },
    { name: 'file_size', data_type: 'number' },
    { name: 'data', data_type: 'json' },
    { name: 'uploaded_by', data_type: 'text' },
];
export const LAB_ATTRIBUTE_DEFS = [
    { name: 'name', data_type: 'text' },
    { name: 'description', data_type: 'text' },
    { name: 'timezone', data_type: 'text' },
];
/**
 * All entity attribute definitions keyed by entity type.
 * Used by the server to register attributes at startup.
 */
export const ENTITY_ATTRIBUTE_DEFS = {
    sample: SAMPLE_ATTRIBUTE_DEFS,
    test: TEST_ATTRIBUTE_DEFS,
    assay: ASSAY_ATTRIBUTE_DEFS,
    order: ORDER_ATTRIBUTE_DEFS,
    attachment: ATTACHMENT_ATTRIBUTE_DEFS,
    lab: LAB_ATTRIBUTE_DEFS,
};
